// Copyright (c) Microsoft Corporation. All rights reserved.
// Licensed under the MIT License.
import { __extends } from "tslib";
import dynamicProto from "@microsoft/dynamicproto-js";
import { BaseTelemetryPlugin, BreezeChannelIdentifier, EventPersistence, EventsDiscardedReason, _throwInternal, arrForEach, createOfflineListener, createProcessTelemetryContext, createUniqueNamespace, dateNow, mergeEvtNamespace, onConfigChange, runTargetUnload } from "@microsoft/applicationinsights-core-js";
import { createAsyncPromise, createTaskScheduler } from "@nevware21/ts-async";
import { arrSlice, isFunction, isString, mathFloor, mathMax, mathMin, objDeepFreeze, objForEachKey, scheduleTimeout } from "@nevware21/ts-utils";
import { EVT_DISCARD_STR, EVT_SENT_STR, EVT_STORE_STR, batchDropNotification, callNotification, isGreaterThanZero, isValidPersistenceLevel } from "./Helpers/Utils";
import { InMemoryBatch } from "./InMemoryBatch";
import { OfflineBatchHandler } from "./OfflineBatchHandler";
import { Sender } from "./Sender";
import { _DYN_ADD_EVENT, _DYN_BATCH_HANDLER, _DYN_CANCEL, _DYN_CLEAR, _DYN_CRITICAL_CNT, _DYN_DATA, _DYN_DIAG_LOG, _DYN_GET_ITEMS, _DYN_INITIALIZE, _DYN_INSTRUMENTATION_KEY, _DYN_IS_COMPLETELY_IDLE, _DYN_IS_ONLINE, _DYN_LENGTH, _DYN_MIN_PERSISTENCE_CACH1, _DYN_ONUNLOAD_FLUSH, _DYN_OVERRIDE_INSTRUMENTA2, _DYN_PERSISTENCE, _DYN_PROCESS_NEXT, _DYN_PUSH, _DYN_REFRESH, _DYN_SEND_NEXT_BATCH, _DYN_STATE, _DYN_TEARDOWN } from "./__DynamicConstants";
var version = '0.4.4';
var DefaultOfflineIdentifier = "OfflineChannel";
var DefaultBatchInterval = 15000;
var DefaultInMemoMaxTime = 15000;
var PostChannelIdentifier = "PostChannel";
var PersistenceKeys = [EventPersistence.Critical, EventPersistence.Normal];
var undefValue = undefined;
var DefaultBatchSizeLimitBytes = 63000; // approx 64kb (the current Edge, Firefox and Chrome max limit)
var defaultOfflineChannelConfig = objDeepFreeze({
    maxStorageSizeInBytes: { isVal: isGreaterThanZero, v: 5000000 },
    storageKey: undefValue,
    minPersistenceLevel: { isVal: isValidPersistenceLevel, v: EventPersistence.Normal },
    providers: [1 /* eStorageProviders.LocalStorage */, 3 /* eStorageProviders.IndexedDb */],
    indexedDbName: undefValue,
    maxStorageItems: { isVal: isGreaterThanZero, v: undefValue },
    inMemoMaxTime: { isVal: isGreaterThanZero, v: DefaultInMemoMaxTime },
    maxRetry: { isVal: isGreaterThanZero, v: 1 },
    maxBatchsize: { isVal: isGreaterThanZero, v: DefaultBatchSizeLimitBytes },
    maxSentBatchInterval: { isVal: isGreaterThanZero, v: DefaultBatchInterval },
    primaryOnlineChannelId: [BreezeChannelIdentifier, PostChannelIdentifier],
    overrideInstrumentationKey: undefValue,
    senderCfg: {},
    splitEvts: false
});
//TODO: add tests for sharedAnanlytics
var OfflineChannel = /** @class */ (function (_super) {
    __extends(OfflineChannel, _super);
    function OfflineChannel() {
        var _this = _super.call(this) || this;
        _this.identifier = DefaultOfflineIdentifier;
        _this.priority = 1000; // before channel (post = 1011 and sender = 1001, teechannel = 999, localstorage:  1009)
        _this.version = version;
        dynamicProto(OfflineChannel, _this, function (_self, _base) {
            // Internal properties used for tracking the current state, these are "true" internal/private properties for this instance
            var _hasInitialized;
            var _paused;
            var _sender;
            var _urlCfg;
            var _offlineListener;
            var _inMemoFlushTimer;
            var _inMemoTimerOut;
            var _diagLogger;
            var _endpoint;
            var _maxBatchSize;
            var _sendNextBatchTimer;
            var _convertUndefined;
            var _retryAt;
            var _maxBatchInterval;
            var _consecutiveErrors;
            var _senderInst;
            var _taskScheduler;
            var _offineSupport;
            var _primaryChannelId;
            var _overrideIkey;
            var _evtsLimitInMemo;
            var _notificationManager;
            var _isLazyInit;
            var _dependencyPlugin;
            var _splitEvts;
            var _inMemoMap;
            _initDefaults();
            _self[_DYN_INITIALIZE /* @min:%2einitialize */] = function (coreConfig, core, extensions, pluginChain) {
                if (!_hasInitialized) {
                    _base[_DYN_INITIALIZE /* @min:%2einitialize */](coreConfig, core, extensions);
                    // keep setting hasInitialized status here to avoid re-initialize
                    _hasInitialized = true;
                    _diagLogger = _self[_DYN_DIAG_LOG /* @min:%2ediagLog */]();
                    var evtNamespace = mergeEvtNamespace(createUniqueNamespace("OfflineSender"), core.evtNamespace && core.evtNamespace());
                    _offlineListener = createOfflineListener(evtNamespace); // TODO: add config to be passed
                    _taskScheduler = createTaskScheduler(createAsyncPromise, "offline channel");
                    _notificationManager = core.getNotifyMgr();
                }
                // TODO: add refresh inside timer until dependency plugin is initialized
                scheduleTimeout(function () {
                    try {
                        var _dependencyPlugin_1 = _getDependencyPlugin(coreConfig, core);
                        // make sure that online sender is initialized
                        if (!_isLazyInit && _dependencyPlugin_1 && _dependencyPlugin_1.isInitialized()) {
                            _isLazyInit = true;
                            _createUrlConfig(coreConfig, core, extensions, pluginChain);
                            var ctx = _getCoreItemCtx(coreConfig, core, extensions, pluginChain);
                            _sender[_DYN_INITIALIZE /* @min:%2einitialize */](coreConfig, core, ctx, _diagLogger, _primaryChannelId, _self._unloadHooks);
                            _senderInst = _sender.getXhrInst();
                            _offlineListener.addListener(function (val) {
                                if (!val[_DYN_IS_ONLINE /* @min:%2eisOnline */]) {
                                    _sendNextBatchTimer && _sendNextBatchTimer[_DYN_CANCEL /* @min:%2ecancel */]();
                                }
                                else {
                                    _setSendNextTimer();
                                }
                            });
                            // need it for first time to confirm if there are any events
                            _setSendNextTimer();
                        }
                    }
                    catch (e) {
                        // eslint-disable-next-line no-empty
                    }
                }, 0);
            };
            _self.processTelemetry = function (evt, itemCtx) {
                try {
                    var onlineStatus = _offlineListener[_DYN_IS_ONLINE /* @min:%2eisOnline */]();
                    itemCtx = itemCtx || _self._getTelCtx(itemCtx);
                    if (!!onlineStatus || !_offineSupport || !_endpoint) {
                        // if we can't get url from online sender or core config, process next
                        _self[_DYN_PROCESS_NEXT /* @min:%2eprocessNext */](evt, itemCtx);
                        return;
                    }
                    if (_hasInitialized && !_paused) {
                        var shouldProcess = true;
                        if (isFunction(_offineSupport.shouldProcess)) {
                            shouldProcess = _offineSupport.shouldProcess(evt);
                        }
                        if (!shouldProcess) {
                            _self[_DYN_PROCESS_NEXT /* @min:%2eprocessNext */](evt, itemCtx);
                            return;
                        }
                        var item = evt;
                        //TODO: add function to better get level
                        item[_DYN_PERSISTENCE /* @min:%2epersistence */] = item[_DYN_PERSISTENCE /* @min:%2epersistence */] || (item.baseData && item.baseData[_DYN_PERSISTENCE /* @min:%2epersistence */]) || EventPersistence.Normal; // in case the level is in baseData
                        if (_shouldCacheEvent(_urlCfg, item) && _inMemoMap) {
                            if (_overrideIkey) {
                                item.iKey = _overrideIkey;
                            }
                            var added = _addEvtToMap(item);
                            // inMemo is full
                            if (!added) {
                                _flushInMemoItems(false, item[_DYN_PERSISTENCE /* @min:%2epersistence */]);
                                var retry = _addEvtToMap(item);
                                if (!retry) {
                                    _evtDropNotification([evt], EventsDiscardedReason.QueueFull);
                                    _throwInternal(_diagLogger, 2 /* eLoggingSeverity.WARNING */, 105 /* _eInternalMessageId.InMemoryStorageBufferFull */, "Maximum offline in-memory buffer size reached", true);
                                }
                            }
                            // start timer when the first should-cache event added
                            _setupInMemoTimer();
                        }
                        else {
                            // if should not cache,send event drop notification
                            _evtDropNotification([item], EventsDiscardedReason.InvalidEvent);
                        }
                        return;
                    }
                }
                catch (e) {
                    // eslint-disable-next-line no-empty
                }
                // hand off the telemetry item to the next plugin
                _self[_DYN_PROCESS_NEXT /* @min:%2eprocessNext */](evt, itemCtx);
            };
            _self.pause = function () {
                _paused = true;
                _clearScheduledTimer();
                _consecutiveErrors = 0;
                _retryAt = null;
            };
            _self.resume = function () {
                _paused = false;
                _clearScheduledTimer();
                _setupInMemoTimer();
                _setSendNextTimer();
            };
            _self[_DYN_ONUNLOAD_FLUSH /* @min:%2eonunloadFlush */] = function () {
                if (!_paused) {
                    while (_hasEvts()) {
                        _flushInMemoItems(true);
                    }
                    // TODO: unloadprovider might send events out of order
                }
            };
            _self.flush = function (sync, callBack, sendReason) {
                // TODO: should we implement normal flush
                return _self[_DYN_ONUNLOAD_FLUSH /* @min:%2eonunloadFlush */]();
            };
            _self.getOfflineListener = function () {
                return _offlineListener;
            };
            _self[_DYN_SEND_NEXT_BATCH /* @min:%2esendNextBatch */] = function () {
                // TODO: add callback function
                return _offineSupport && _setSendNextTimer();
            };
            _self._doTeardown = function (unloadCtx, unloadState) {
                _self[_DYN_ONUNLOAD_FLUSH /* @min:%2eonunloadFlush */]();
                _offlineListener && runTargetUnload(_offlineListener, false);
                var handler = _urlCfg && _urlCfg[_DYN_BATCH_HANDLER /* @min:%2ebatchHandler */];
                handler && handler[_DYN_TEARDOWN /* @min:%2eteardown */]();
                _clearScheduledTimer();
                _initDefaults();
            };
            _self["_getDbgPlgTargets"] = function () {
                return [_urlCfg, _inMemoMap, _senderInst, _inMemoFlushTimer, _sendNextBatchTimer];
            };
            function _initDefaults() {
                _hasInitialized = false;
                _paused = false;
                _sender = new Sender();
                _urlCfg = null;
                _offlineListener = null;
                _diagLogger = null;
                _endpoint = null;
                _convertUndefined = undefValue;
                _maxBatchSize = null;
                _sendNextBatchTimer = null;
                _consecutiveErrors = null;
                _retryAt = null;
                _maxBatchInterval = null;
                _senderInst = null;
                _offineSupport = null;
                _primaryChannelId = null;
                _overrideIkey = null;
                _evtsLimitInMemo = null;
                _isLazyInit = false;
                _dependencyPlugin = null;
                _splitEvts = false;
                _inMemoMap = null;
            }
            function _shouldCacheEvent(urlConfig, item) {
                if ((item[_DYN_PERSISTENCE /* @min:%2epersistence */]) < urlConfig[_DYN_MIN_PERSISTENCE_CACH1 /* @min:%2eminPersistenceCacheLevel */]) {
                    return false;
                }
                return true;
            }
            function _setupInMemoTimer() {
                if (!_inMemoFlushTimer) {
                    _inMemoFlushTimer = scheduleTimeout(function () {
                        _flushInMemoItems();
                        var hasEvts = _hasEvts();
                        if (hasEvts && _inMemoFlushTimer) {
                            _inMemoFlushTimer[_DYN_REFRESH /* @min:%2erefresh */]();
                        }
                        _setSendNextTimer();
                    }, _inMemoTimerOut);
                    _inMemoFlushTimer.unref();
                }
                else {
                    // Restart the timer if not already running https://nevware21.github.io/ts-utils/typedoc/interfaces/ITimerHandler.html#enabled
                    _inMemoFlushTimer.enabled = true;
                }
            }
            //flush only flush max batch size event, may still have events lefts
            function _flushInMemoItems(unload, mapKey) {
                try {
                    // TODO: add while loop to flush everything
                    var hasEvts_1 = false;
                    // can use objForEachKey(_inMemoMap, (key)), but keys returned by this function is always string
                    arrForEach(PersistenceKeys, function (key) {
                        var inMemoBatch = _inMemoMap[key];
                        var inMemo = inMemoBatch;
                        var evts = inMemo && inMemo[_DYN_GET_ITEMS /* @min:%2egetItems */]();
                        if (!evts || !evts[_DYN_LENGTH /* @min:%2elength */]) {
                            return;
                        }
                        if (_splitEvts && mapKey && mapKey !== key) {
                            // if split evts is set to true
                            // specific mapkey is defined
                            // for key !== mapkey, only check if there are any events left in order to refresh timer
                            hasEvts_1 = hasEvts_1 || !!evts[_DYN_LENGTH /* @min:%2elength */];
                            return;
                        }
                        var payloadArr = [];
                        var size = 0;
                        var idx = -1;
                        var criticalCnt = 0;
                        arrForEach(evts, function (evt, index) {
                            var curEvt = evt;
                            idx = index;
                            var payload = _getPayload(curEvt);
                            size += payload[_DYN_LENGTH /* @min:%2elength */];
                            if (size > _maxBatchSize) {
                                return;
                            }
                            if (curEvt[_DYN_PERSISTENCE /* @min:%2epersistence */] == EventPersistence.Critical) {
                                criticalCnt++;
                            }
                            idx = index;
                            payloadArr[_DYN_PUSH /* @min:%2epush */](payload);
                        });
                        if (!payloadArr[_DYN_LENGTH /* @min:%2elength */]) {
                            return;
                        }
                        var sentItems = evts.slice(0, idx + 1);
                        if (idx + 1 < evts[_DYN_LENGTH /* @min:%2elength */]) {
                            // keep track if there is any remaining events
                            hasEvts_1 = true;
                        }
                        _inMemoMap[key] = inMemoBatch.createNew(_endpoint, arrSlice(inMemo[_DYN_GET_ITEMS /* @min:%2egetItems */](), idx + 1), _evtsLimitInMemo);
                        var payloadData = null;
                        if (_offineSupport && _offineSupport.createOneDSPayload) {
                            payloadData = _offineSupport.createOneDSPayload(sentItems);
                            if (payloadData) {
                                payloadData[_DYN_CRITICAL_CNT /* @min:%2ecriticalCnt */] = criticalCnt;
                            }
                        }
                        else {
                            payloadData = _constructPayloadData(payloadArr, criticalCnt);
                        }
                        var callback = function (res) {
                            if (!res || !res[_DYN_STATE /* @min:%2estate */]) {
                                return null;
                            }
                            var state = res[_DYN_STATE /* @min:%2estate */];
                            if (state == 2 /* eBatchStoreStatus.Failure */) {
                                if (!unload) {
                                    // for unload, just try to add each batch once
                                    arrForEach(sentItems, function (item) {
                                        _addEvtToMap(item);
                                    });
                                    _setupInMemoTimer();
                                }
                                else {
                                    // unload, drop events
                                    _evtDropNotification(sentItems, EventsDiscardedReason.NonRetryableStatus);
                                }
                            }
                            else {
                                // if eBatchStoreStatus is success
                                _storeNotification(sentItems);
                            }
                        };
                        if (payloadData && _urlCfg && _urlCfg[_DYN_BATCH_HANDLER /* @min:%2ebatchHandler */]) {
                            var promise = _urlCfg[_DYN_BATCH_HANDLER /* @min:%2ebatchHandler */].storeBatch(payloadData, callback, unload);
                            _queueStorageEvent("storeBatch", promise);
                        }
                    });
                    // this is outside loop
                    if (!hasEvts_1) {
                        _inMemoFlushTimer && _inMemoFlushTimer[_DYN_CANCEL /* @min:%2ecancel */]();
                    }
                }
                catch (e) {
                    // eslint-disable-next-line no-empty
                }
                return null;
            }
            function _setSendNextTimer() {
                var isOnline = _offlineListener && _offlineListener[_DYN_IS_ONLINE /* @min:%2eisOnline */]();
                if (!_sendNextBatchTimer) {
                    var retryInterval = _retryAt ? mathMax(0, _retryAt - dateNow()) : 0;
                    var timerValue = mathMax(_maxBatchInterval, retryInterval);
                    _sendNextBatchTimer = scheduleTimeout(function () {
                        if (_offlineListener && _offlineListener[_DYN_IS_ONLINE /* @min:%2eisOnline */]()) {
                            // is no isCompletelyIdle function is available, assume we can send
                            if (isFunction(_sender[_DYN_IS_COMPLETELY_IDLE /* @min:%2eisCompletelyIdle */]) && !_sender[_DYN_IS_COMPLETELY_IDLE /* @min:%2eisCompletelyIdle */]()) {
                                _sendNextBatchTimer && _sendNextBatchTimer[_DYN_REFRESH /* @min:%2erefresh */]();
                            }
                            else {
                                var callback = function (res) {
                                    var state = res && res[_DYN_STATE /* @min:%2estate */];
                                    if (state !== 1 /* eBatchSendStatus.Complete */) {
                                        _consecutiveErrors++;
                                    }
                                    var data = res && res[_DYN_DATA /* @min:%2edata */];
                                    if (state === 1 /* eBatchSendStatus.Complete */ && data) {
                                        // if status is complete and data is null, means no data
                                        _sentNotification(data);
                                        _sendNextBatchTimer && _sendNextBatchTimer[_DYN_REFRESH /* @min:%2erefresh */]();
                                    }
                                    if (state === 3 /* eBatchSendStatus.Drop */) {
                                        batchDropNotification(_notificationManager, 1, 1 /* eBatchDiscardedReason.NonRetryableStatus */);
                                    }
                                };
                                if (_urlCfg && _urlCfg[_DYN_BATCH_HANDLER /* @min:%2ebatchHandler */]) {
                                    var promise = _urlCfg.batchHandler[_DYN_SEND_NEXT_BATCH /* @min:%2esendNextBatch */](callback, false, _senderInst);
                                    _queueStorageEvent("sendNextBatch", promise);
                                }
                            }
                        }
                        else {
                            _sendNextBatchTimer && _sendNextBatchTimer[_DYN_CANCEL /* @min:%2ecancel */]();
                        }
                        // if offline, do nothing;
                    }, timerValue);
                    _sendNextBatchTimer.unref();
                }
                else {
                    // only restart it when online
                    if (isOnline) {
                        _sendNextBatchTimer.enabled = true;
                        _sendNextBatchTimer[_DYN_REFRESH /* @min:%2erefresh */]();
                    }
                    // if offline, do noting
                }
            }
            function _clearScheduledTimer() {
                _inMemoFlushTimer && _inMemoFlushTimer[_DYN_CANCEL /* @min:%2ecancel */]();
                _sendNextBatchTimer && _sendNextBatchTimer[_DYN_CANCEL /* @min:%2ecancel */]();
                _inMemoFlushTimer = null;
                _sendNextBatchTimer = null;
            }
            function _queueStorageEvent(taskName, task) {
                if (_taskScheduler) {
                    _taskScheduler.queue(function () {
                        return task;
                    }, taskName).catch(function (reason) {
                        // Just handling any rejection to avoid an unhandled rejection event
                    });
                }
            }
            function _setRetryTime(linearFactor) {
                if (linearFactor === void 0) { linearFactor = 1; }
                var SlotDelayInSeconds = 10;
                var delayInSeconds;
                if (_consecutiveErrors <= 1) {
                    delayInSeconds = SlotDelayInSeconds;
                }
                else {
                    var backOffSlot = (Math.pow(2, _consecutiveErrors) - 1) / 2;
                    // tslint:disable-next-line:insecure-random
                    var backOffDelay = mathFloor(Math.random() * backOffSlot * SlotDelayInSeconds) + 1;
                    backOffDelay = linearFactor * backOffDelay;
                    delayInSeconds = mathMax(mathMin(backOffDelay, 3600), SlotDelayInSeconds);
                }
                // TODO: Log the backoff time like the C# version does.
                var retryAfterTimeSpan = dateNow() + (delayInSeconds * 1000);
                // TODO: Log the retry at time like the C# version does.
                _retryAt = retryAfterTimeSpan;
            }
            function _getCoreItemCtx(coreConfig, core, extensions, pluginChain) {
                if (coreConfig) {
                    // Make sure the extensionConfig exists
                    coreConfig.extensionConfig = coreConfig.extensionConfig || {};
                }
                if (!pluginChain && core) {
                    // Get the first plugin from the core
                    pluginChain = core.getProcessTelContext().getNext();
                }
                var nextPlugin = null;
                var rootNext = _self._getTelCtx().getNext();
                if (rootNext) {
                    nextPlugin = rootNext.getPlugin();
                }
                return createProcessTelemetryContext(pluginChain, coreConfig, core, nextPlugin);
            }
            function _getPayload(evt) {
                try {
                    if (evt) {
                        return _offineSupport && _offineSupport.serialize(evt, _convertUndefined);
                    }
                }
                catch (e) {
                    // eslint-disable-next-line no-empty
                }
                return null;
            }
            function _constructPayloadData(payloadArr, criticalCnt) {
                try {
                    if (!_offineSupport) {
                        return null;
                    }
                    var cnt = criticalCnt || 0;
                    var payload = _offineSupport.batch(payloadArr);
                    var payloadData = _offineSupport.createPayload && _offineSupport.createPayload(payload);
                    if (payloadData) {
                        payloadData[_DYN_CRITICAL_CNT /* @min:%2ecriticalCnt */] = cnt;
                        return payloadData;
                    }
                }
                catch (e) {
                    // eslint-disable-next-line no-empty
                }
                return null;
            }
            function _createUrlConfig(coreConfig, core, extensions, pluginChain) {
                _self._addHook(onConfigChange(coreConfig, function (details) {
                    if (!isString(coreConfig[_DYN_INSTRUMENTATION_KEY /* @min:%2einstrumentationKey */]) || !isString(coreConfig.endpointUrl)) {
                        // if ikey or endpointUrl is promise, delay initialization
                        _self.pause();
                        return;
                    }
                    if (_paused) {
                        _self.resume();
                    }
                    var storageConfig = null;
                    var theConfig = details.cfg;
                    var ctx = createProcessTelemetryContext(null, theConfig, core);
                    storageConfig = ctx.getExtCfg(_self.identifier, defaultOfflineChannelConfig);
                    // let channelIds = storageConfig.primaryOnlineChannelId;
                    var onlineUrl = null;
                    var channel = _getDependencyPlugin(coreConfig, core);
                    if (!!channel.isInitialized() && isFunction(channel.getOfflineSupport)) {
                        _offineSupport = channel.getOfflineSupport();
                        onlineUrl = isFunction(_offineSupport && _offineSupport.getUrl) && _offineSupport.getUrl();
                    }
                    else {
                        return;
                    }
                    _overrideIkey = storageConfig[_DYN_OVERRIDE_INSTRUMENTA2 /* @min:%2eoverrideInstrumentationKey */];
                    var urlConfig = _urlCfg;
                    var curUrl = onlineUrl || coreConfig.endpointUrl || _endpoint;
                    // NOTE: should add default endpoint value to core as well
                    if (curUrl !== _endpoint) {
                        var coreRootCtx = _getCoreItemCtx(coreConfig, core, extensions, pluginChain);
                        var providerContext = {
                            itemCtx: coreRootCtx,
                            storageConfig: storageConfig,
                            id: _self.id,
                            endpoint: curUrl,
                            notificationMgr: _notificationManager
                        };
                        var oriHandler = _urlCfg && _urlCfg[_DYN_BATCH_HANDLER /* @min:%2ebatchHandler */];
                        try {
                            oriHandler && oriHandler[_DYN_TEARDOWN /* @min:%2eteardown */]();
                        }
                        catch (e) {
                            // eslint-disable-next-line no-empty
                        }
                        var handler = new OfflineBatchHandler(_self[_DYN_DIAG_LOG /* @min:%2ediagLog */](), _self._unloadHooks);
                        handler[_DYN_INITIALIZE /* @min:%2einitialize */](providerContext);
                        urlConfig = {
                            iKey: coreConfig[_DYN_INSTRUMENTATION_KEY /* @min:%2einstrumentationKey */],
                            url: curUrl,
                            minPersistenceCacheLevel: storageConfig.minPersistenceLevel,
                            coreRootCtx: coreRootCtx,
                            providerContext: providerContext,
                            batchHandler: handler
                        };
                        _evtsLimitInMemo = storageConfig.eventsLimitInMem;
                        _inMemoMap = _inMemoMap || {};
                        var newMap_1 = {};
                        // transfer previous events to new buffer
                        arrForEach(PersistenceKeys, function (key) {
                            // when init map, we will initize a in memo batch for each EventPersistence key
                            // evts to be transferred to new inMemo map
                            var evts = null;
                            if (_inMemoMap && _inMemoMap[key]) {
                                var inMemoBatch = _inMemoMap[key];
                                var curEvts = inMemoBatch && inMemoBatch[_DYN_GET_ITEMS /* @min:%2egetItems */]();
                                if (curEvts && curEvts[_DYN_LENGTH /* @min:%2elength */]) {
                                    evts = arrSlice(curEvts);
                                    inMemoBatch && inMemoBatch[_DYN_CLEAR /* @min:%2eclear */]();
                                }
                            }
                            newMap_1[key] = new InMemoryBatch(_self[_DYN_DIAG_LOG /* @min:%2ediagLog */](), curUrl, evts, _evtsLimitInMemo);
                        });
                        _inMemoMap = newMap_1;
                        _inMemoTimerOut = storageConfig.inMemoMaxTime;
                        var onlineConfig = ctx.getExtCfg(_primaryChannelId, {}) || {};
                        _convertUndefined = onlineConfig.convertUndefined;
                        _endpoint = curUrl;
                        _setRetryTime();
                        _maxBatchInterval = storageConfig.maxSentBatchInterval;
                        _maxBatchSize = storageConfig.maxBatchsize;
                        _splitEvts = storageConfig.splitEvts;
                    }
                    _urlCfg = urlConfig;
                    _endpoint = curUrl;
                }));
            }
            function _getDependencyPlugin(coreConfig, core) {
                if (!_dependencyPlugin) {
                    var ctx = createProcessTelemetryContext(null, coreConfig, core);
                    var storageConfig = ctx.getExtCfg(_self.identifier, defaultOfflineChannelConfig);
                    var channelIds = storageConfig.primaryOnlineChannelId;
                    if (channelIds && channelIds[_DYN_LENGTH /* @min:%2elength */]) {
                        arrForEach(channelIds, function (id) {
                            var plugin = core.getPlugin(id);
                            var channel = plugin && plugin.plugin;
                            if (channel) {
                                _dependencyPlugin = channel;
                                return;
                            }
                        });
                    }
                }
                return _dependencyPlugin;
            }
            function _hasEvts() {
                var hasEvts = false;
                objForEachKey(_inMemoMap, function (key) {
                    var inMemoBatch = _inMemoMap[key];
                    if (inMemoBatch && inMemoBatch.count()) {
                        hasEvts = true;
                        return -1;
                    }
                });
                return hasEvts;
            }
            function _addEvtToMap(item) {
                // if split evts is false, all events will be saved into EventPersistence.Normal batch
                var mapKey = EventPersistence.Normal;
                if (_splitEvts && item[_DYN_PERSISTENCE /* @min:%2epersistence */]) {
                    mapKey = item[_DYN_PERSISTENCE /* @min:%2epersistence */];
                }
                var inMemoBatch = _inMemoMap[mapKey];
                return inMemoBatch[_DYN_ADD_EVENT /* @min:%2eaddEvent */](item);
            }
            function _callNotification(evtName, theArgs) {
                callNotification(_notificationManager, evtName, theArgs);
            }
            function _evtDropNotification(events, reason) {
                if (events && events[_DYN_LENGTH /* @min:%2elength */]) {
                    _callNotification(EVT_DISCARD_STR, [events, reason]);
                }
                return;
            }
            function _sentNotification(batch) {
                if (batch && batch[_DYN_DATA /* @min:%2edata */]) {
                    _callNotification(EVT_SENT_STR, [batch]);
                }
                return;
            }
            function _storeNotification(events) {
                if (events && events[_DYN_LENGTH /* @min:%2elength */]) {
                    _callNotification(EVT_STORE_STR, [events]);
                }
                return;
            }
        });
        return _this;
    }
    /* ================================================================================================================
     * DO NOT add any code to these empty implementations as any code defined here will be removed, as
     * this class is using @dynamicProto which will implement the methods during the execution of the
     * dynamicProto() in the constructor.
     *
     * The final generated files will also have these implementations removed as part of the packaging process.
     *
     * These empty definitions exists only to keep the generated TypeScript definition files aligned with the
     * actual resulting implementation, this is so that TS is still happy to create extension classes from the
     * resulting definitions.
     *
     * This also keeps the generated *.d.ts files and documentation the same as they where prior to using dynamicProto()
     */
    /**
     * The function does the initial set up. It adds a notification listener to determine which events to remove.
     * @param coreConfig - The core configuration.
     * @param core       - The AppInsights core.
     * @param extensions - An array of all the plugins being used.
     */
    OfflineChannel.prototype.initialize = function (coreConfig, core, extensions, pluginChain) {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
    };
    /**
     * Process an event to add it to the local storage and then pass it to the next plugin.
     * @param event - The event that needs to be stored.
     * @param itemCtx - This is the context for the current request, ITelemetryPlugin instances
     * can optionally use this to access the current core instance or define / pass additional information
     * to later plugins (vs appending items to the telemetry item)
     */
    OfflineChannel.prototype.processTelemetry = function (evt, itemCtx) {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
    };
    // ================================================================================================================
    /**
     * Pauses the adding of new events to the plugin. Also calls pause on the next
     * plugin.
     */
    OfflineChannel.prototype.pause = function () {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
    };
    /**
     * Resumes the adding of new events to the plugin. Also calls resume on
     * the next plugin. Adds all events in storage to the next plugin.
     */
    OfflineChannel.prototype.resume = function () {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
    };
    /**
     * Get offline listener
     * @returns offline listener
     */
    OfflineChannel.prototype.getOfflineListener = function () {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
        return null;
    };
    /**
     *No op
     */
    OfflineChannel.prototype.flush = function (sync, callBack, sendReason) {
        // @DynamicProtoStub - DO NOT add any code as this will be removed during packaging
    };
    /**
     * Flush the batched events synchronously (if possible -- based on configuration).
     * Will not flush if the Send has been paused.
     */
    OfflineChannel.prototype.onunloadFlush = function () {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
    };
    /**
     * Flush the next stored event batch
     */
    OfflineChannel.prototype.sendNextBatch = function () {
        // @DynamicProtoStub -- DO NOT add any code as this will be removed during packaging
    };
    return OfflineChannel;
}(BaseTelemetryPlugin));
export { OfflineChannel };
//# sourceMappingURL=OfflineChannel.js.map